import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetProjectDetail, getServerPageGetProjectSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	BodyTextFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment,
	ProjectDetailSingleFieldsFragment,
	ProjectFieldsFragment
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';

interface IProjectDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	projectDetail?: ProjectDetailSingleFieldsFragment;
	projectBySlug?: ProjectFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
}

const ProjectDetailPage = (props: IProjectDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.projectBySlug || null,
		pageQuery: () => getServerPageGetProjectDetail({ variables: props.variables }, client)
	});

	// Dirty fix for quick polish page
	if (page?.fullPath === 'projecten/life-co-2-sand-polski' && typeof document !== 'undefined') {
		document.documentElement.setAttribute('lang', 'pl');

		const selected = document.querySelector('#project') as HTMLElement | null;

		if (selected !== null) {
			selected.style.fontFamily = 'Fedra Serif LCG';
		}
	}

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.projectBySlug?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Project' ? (
				<div id="project">
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page?.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader
							module={{
								title: page?.header?.title || '',
								backgroundColor: page?.header?.backgroundColor,
								excerpt: page?.header?.excerpt || '',
								cover: page?.header?.cover
							}}
							fullPath={page?.fullPath || ''}
						/>
					</BaseModule>
					<BaseModule defaultModuleOptions={page?.bodyText?.defaultModuleOptions}>
						<BodyText module={page?.bodyText as BodyTextFieldsFragment} updatedAt={page?.updatedAt} />
					</BaseModule>
					<ModuleBuilder modules={page?.modules || []} fullPath={page?.fullPath || ''} />
					<ModuleBuilder modules={props.projectDetail?.modules || []} fullPath={page?.fullPath || ''} />
				</div>
			) : (
				<PasswordLayout locale={props.variables?.locale} slug={props.variables?.slug}>
					<PasswordModule />
				</PasswordLayout>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetProjectSlugs({}, client);
	const slugs = result.props.data.findAllPaths;

	const paths = slugs?.map((slug) => ({
		params: {
			slug: slug?.fullSlug || '/'
		},
		locale: slug?.locale || ctx.defaultLocale
	}));

	return {
		paths: paths || [],
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug as string;
		const variables = {
			slug,
			locale,
			publicationState
		};

		if (publicationState === 'PREVIEW') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetProjectDetail({ variables }, client);
		const { pageInformation, projectDetail, projectBySlug } = result.props.data;

		const initialReduxState = {
			settings: {
				...settingsInitialState,
				publicationState,
				contentType: 'project',
				contentTypeId: projectBySlug?.data?.id || null
			}
		};

		if (projectBySlug === null) {
			return {
				props: {
					variables,
					pageInformation: pageInformation?.data?.attributes,
					page: null,
					initialReduxState
				},
				revalidate: Number(process.env.REVALIDATE_TIME_IN_SECONDS)
			};
		}

		if (!projectBySlug) {
			return {
				notFound: true,
				props: {
					initialReduxState: {
						settings: { ...settingsInitialState, publicationState }
					}
				}
			};
		}

		return {
			props: {
				key: projectBySlug?.data?.id,
				pageInformation: pageInformation?.data?.attributes || null,
				projectDetail: projectDetail?.data?.attributes || null,
				projectBySlug: projectBySlug?.data?.attributes || null,
				id: projectBySlug?.data?.id,
				initialReduxState
			},
			revalidate: Number(process.env.REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in project detail', error);

		return {
			notFound: true
		};
	}
};

export default ProjectDetailPage;
